import React, { useState, useRef, useEffect } from "react";

import { format, parseISO } from "date-fns";

import ComparisonSelector from "./ComparisonSelector";
import DateRangePicker from "./DateRangePicker";

const presetLabels = {
  "7days": "last 7 days",
  "30days": "last 30 days",
  "90days": "last 90 days",
  "12months": "last 12 months",
  ytd: "year to date",
};

const ReportHeader = ({
  dateRange,
  onDateRangeChange,
  comparison,
  onComparisonChange,
  onExport,
  onRefresh,
  refreshing,
}) => {
  const [showExportMenu, setShowExportMenu] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowExportMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Build the status text based on current date range selection
  const getStatusText = () => {
    if (dateRange.type === "custom" && dateRange.start && dateRange.end) {
      const startFormatted = format(parseISO(dateRange.start), "MMM d, yyyy");
      const endFormatted = format(parseISO(dateRange.end), "MMM d, yyyy");

      return `Showing data for ${startFormatted} - ${endFormatted}`;
    }

    const label = presetLabels[dateRange.preset] || "last 30 days";

    return `Showing data for the ${label}`;
  };

  return (
    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4">
      {/* Left: Status text */}
      <div>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {getStatusText()}
        </p>
      </div>

      {/* Right: Controls */}
      <div className="flex flex-wrap items-center gap-2 sm:gap-3 w-full sm:w-auto">
        <div className="flex flex-wrap items-center gap-2">
          <DateRangePicker value={dateRange} onChange={onDateRangeChange} />
          <ComparisonSelector
            value={comparison}
            onChange={onComparisonChange}
          />
        </div>

        <div className="flex gap-2">
          {/* Export dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              className="btn btn-ghost flex-1 sm:flex-none"
              onClick={() => setShowExportMenu((prev) => !prev)}
            >
              <i className="fas fa-download mr-2" />
              <span className="hidden sm:inline">Export Report</span>
              <span className="sm:hidden">Export</span>
              <i className="fas fa-chevron-down ml-2 text-xs" />
            </button>

            {showExportMenu && (
              <div className="absolute right-0 mt-1 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50 py-1">
                <button
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  onClick={() => {
                    onExport("csv");
                    setShowExportMenu(false);
                  }}
                >
                  <i className="fas fa-file-csv mr-2 text-green-500" />
                  Export as CSV
                </button>
                <button
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  onClick={() => {
                    onExport("pdf");
                    setShowExportMenu(false);
                  }}
                >
                  <i className="fas fa-file-pdf mr-2 text-red-500" />
                  Export as PDF
                </button>
                <button
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  onClick={() => {
                    onExport("fullPdf");
                    setShowExportMenu(false);
                  }}
                >
                  <i className="fas fa-file-alt mr-2 text-blue-500" />
                  Full Report (PDF)
                </button>
              </div>
            )}
          </div>

          {/* Refresh button */}
          <button
            className="btn btn-primary flex-1 sm:flex-none"
            disabled={refreshing}
            onClick={onRefresh}
          >
            <i
              className={`fas fa-sync mr-2 ${refreshing ? "animate-spin" : ""}`}
            />
            <span>Refresh</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReportHeader;
